import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IframeMode } from '../../../../enums/iframe-mode.enum';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private instanceId: string;

  constructor() {
    this.cleanupOrphanedKeys();
    this.instanceId = this.generateInstanceId();
  }

  private generateInstanceId(): string {
    const urlParams = new URLSearchParams(window.location.search);
    const iframeModeParam = urlParams.get('iframeMode');
    
    let prefix = 'none';
    if (iframeModeParam === IframeMode.INLINE || iframeModeParam === 'INLINE') {
      prefix = 'inline';
    } else if (iframeModeParam === IframeMode.WIDGET || iframeModeParam === 'WIDGET') {
      prefix = 'widget';
    }
    
    const randomPart = Math.random().toString(36).slice(2, 11);
    return `${prefix}_${Date.now()}_${randomPart}`;
  }

  private cleanupOrphanedKeys(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const iframeModeParam = urlParams.get('iframeMode');
    
    let prefix = 'none';
    if (iframeModeParam === IframeMode.INLINE || iframeModeParam === 'INLINE') {
      prefix = 'inline';
    } else if (iframeModeParam === IframeMode.WIDGET || iframeModeParam === 'WIDGET') {
      prefix = 'widget';
    }
    
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith(`${prefix}_`) && key.includes('_')) {
        sessionStorage.removeItem(key);
      }
    });
  }

  private getKey(key: string): string {
    return `${this.instanceId}_${key}`;
  }

  setSessionId(id: string) {
    sessionStorage.setItem(this.getKey("sessionId"), id)
  }

  removeSessionId() {
    sessionStorage.removeItem(this.getKey("sessionId"));
  }

  getSessionId() {
    return sessionStorage.getItem(this.getKey("sessionId"));
  }

  setContextInfo(info: string) {
     sessionStorage.setItem(this.getKey("contextInfo"), info); 
  }

  getContextInfo() {
    return sessionStorage.getItem(this.getKey("contextInfo"));
  }

  removeContextInfo() {
    sessionStorage.removeItem(this.getKey("contextInfo"));
  }

  setlastMessageSource(lastMessageSource: string) {
    sessionStorage.setItem(this.getKey("lastMessageSource"), lastMessageSource)
  }

  removeLastMessageSource() {
    sessionStorage.removeItem(this.getKey("lastMessageSource"));
  }

  getLastMessageSource() {
    return sessionStorage.getItem(this.getKey("lastMessageSource"));
  }
  
  setBaseAssetUrl(baseAssetUrl: string) {
    sessionStorage.setItem(this.getKey("baseAssetUrl"), baseAssetUrl);  
  }
  
  getBaseAssetUrl() {
    return sessionStorage.getItem(this.getKey("baseAssetUrl")); 
  }

  // ---------------------------------------------------------------------------
  // Tenant code — canonical embed identifier (tenant.code, sent as X-Tenant-Code).
  // ---------------------------------------------------------------------------

  setTenantCode(tenantCode: string) {
    sessionStorage.setItem(this.getKey("tenantCode"), tenantCode);
  }

  getTenantCode(): string | null {
    return sessionStorage.getItem(this.getKey("tenantCode"));
  }

  // ---------------------------------------------------------------------------
  // Workshop ID — the DMS workshop id of the branch this embed is for, from
  // data-workshop-id / ?workshopId= (sent as X-Workshop-Id). Must be numeric:
  // the backend ignores anything else and falls back to no branch narrowing.
  // ---------------------------------------------------------------------------

  setWorkshopId(workshopId: string) {
    sessionStorage.setItem(this.getKey("workshopId"), workshopId);
  }

  getWorkshopId(): string | null {
    return sessionStorage.getItem(this.getKey("workshopId"));
  }

  clearWorkshopId() {
    sessionStorage.removeItem(this.getKey("workshopId"));
  }

  clearAllSessionData(): void {
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith(this.instanceId)) {
        sessionStorage.removeItem(key);
      }
    });
  }
}
